/**
 * Predicción — catálogo de modelos ML con explicabilidad.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../api/client";
import type { Forecast } from "../api/contracts";
import { runtime } from "../api/runtime";
import { type PuntoSerie, registerTwinRuntime } from "../lib/twin";
import { PageHeader } from "../layout/PageHeader";

interface MlModels {
  modelos: Array<{
    nombre: string;
    tipo: string;
    metrica: string;
    valor: number;
    estado: string;
    confianza: number;
    drift: number;
    features: Array<{ nombre: string; importancia: number }>;
    impacto: string;
  }>;
  forecast_nrw: number[];
  forecast_demanda: number[];
}

/**
 * Deriva una serie histórica horaria determinista a partir del forecast
 * de NRW observado, para alimentar el Gemelo_Digital (R15.3). Los
 * timestamps se espacian una hora desde un instante de referencia fijo,
 * de modo que el forecast del Twin sea reproducible (R15.5).
 */
function historiaDesdeNrw(forecast_nrw: number[]): PuntoSerie[] {
  const baseMs = Date.parse("2025-01-01T00:00:00.000Z");
  return forecast_nrw.map((valor, i) => ({
    ts: new Date(baseMs + i * 3_600_000).toISOString(),
    valor,
  }));
}

export function Prediction(): JSX.Element {
  const q = useQuery<MlModels>({
    queryKey: ["ml.models"],
    queryFn: () => api<MlModels>("/ml/models"),
    staleTime: 60_000,
  });
  const m = q.data;
  const forecast =
    m?.forecast_nrw.map((v, i) => ({ idx: `+${i}m`, nrw: v })) ?? [];

  // Gemelo_Digital: proyección determinista de NRW a 24 h con su nivel de
  // confianza, consumida vía el runtime selector (`runtime.twin()`).
  const HORIZONTE_H = 24;
  const twinQ = useQuery<Forecast | null>({
    queryKey: ["twin.forecast", HORIZONTE_H, m?.forecast_nrw],
    enabled: Boolean(m),
    staleTime: 60_000,
    queryFn: async () => {
      registerTwinRuntime({ historia: historiaDesdeNrw(m!.forecast_nrw) });
      const res = await runtime.twin().forecast(HORIZONTE_H);
      return res.ok ? res.value : null;
    },
  });
  const twinForecast = twinQ.data
    ? twinQ.data.pred.map((p, i) => ({ idx: `+${i + 1}h`, nrw: Number(p.valor.toFixed(2)) }))
    : [];

  return (
    <>
      <PageHeader
        eyebrow="Analítica NRW"
        title="Predicción & Machine Learning"
        subtitle="Modelos en producción con métrica, confianza, drift y explicabilidad."
      />
      {m && (
        <>
          <section className="card" style={{ marginBottom: 12 }}>
            <div className="card__title">Forecast NRW red (6 meses)</div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <AreaChart data={forecast} margin={{ top: 8, right: 8, bottom: 0, left: -6 }}>
                  <defs>
                    <linearGradient id="grad-fc" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--c-success)" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="var(--c-success)" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                  <XAxis dataKey="idx" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} domain={[22, 28]} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--c-surface)",
                      border: "1px solid var(--c-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v: number) => [`${v.toFixed(1)} %`, "NRW proyectado"]}
                  />
                  <Area type="monotone" dataKey="nrw" stroke="var(--c-success)" strokeWidth={2} fill="url(#grad-fc)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          {twinForecast.length > 0 && (
            <section className="card" style={{ marginBottom: 12 }}>
              <div className="card__title">
                <span>Gemelo Digital · Forecast NRW (+{HORIZONTE_H} h)</span>
                <span className="badge badge--info">simulación</span>
              </div>
              <div className="text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                Predicción determinista por regresión lineal simple · confianza{" "}
                <strong className="text-strong">
                  {Math.round((twinQ.data?.confianza ?? 0) * 100)}%
                </strong>
              </div>
              <div style={{ width: "100%", height: 180 }}>
                <ResponsiveContainer>
                  <AreaChart data={twinForecast} margin={{ top: 8, right: 8, bottom: 0, left: -6 }}>
                    <defs>
                      <linearGradient id="grad-twin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--c-accent-500)" stopOpacity={0.5} />
                        <stop offset="95%" stopColor="var(--c-accent-500)" stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
                    <XAxis dataKey="idx" stroke="var(--chart-axis)" tick={{ fontSize: 11 }} />
                    <YAxis stroke="var(--chart-axis)" tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                    <Tooltip
                      contentStyle={{
                        background: "var(--c-surface)",
                        border: "1px solid var(--c-border)",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v: number) => [`${v.toFixed(1)} %`, "NRW proyectado"]}
                    />
                    <Area type="monotone" dataKey="nrw" stroke="var(--c-accent-500)" strokeWidth={2} fill="url(#grad-twin)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </section>
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 12,
            }}
          >
            {m.modelos.map((model) => (
              <section className="card" key={model.nombre}>
                <div className="card__title">
                  <span>{model.nombre}</span>
                  <span className={`badge badge--${model.estado === "champion" ? "accent" : "info"}`}>
                    {model.estado}
                  </span>
                </div>
                <div className="hstack" style={{ gap: 16, marginBottom: 10 }}>
                  <div>
                    <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
                      {model.metrica}
                    </div>
                    <div className="kpi__value" style={{ fontSize: 24 }}>
                      {model.valor}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
                      Confianza
                    </div>
                    <div className="kpi__value" style={{ fontSize: 24 }}>
                      {Math.round(model.confianza * 100)}%
                    </div>
                  </div>
                  <div>
                    <div className="text-muted" style={{ fontSize: 10.5, textTransform: "uppercase" }}>
                      Drift
                    </div>
                    <div className="kpi__value" style={{ fontSize: 24, color: model.drift > 0.06 ? "var(--c-warn)" : "var(--c-text-strong)" }}>
                      {(model.drift * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
                <div className="text-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                  {model.tipo} · impacto: <strong className="text-strong">{model.impacto}</strong>
                </div>
                <div className="card__title" style={{ fontSize: 11 }}>Features relevantes</div>
                {model.features.map((f) => (
                  <div key={f.nombre} style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, marginBottom: 2 }}>
                      <span>{f.nombre}</span>
                      <span className="text-mono text-muted">{Math.round(f.importancia * 100)}%</span>
                    </div>
                    <div className="sla-bar">
                      <div
                        className="sla-bar__fill"
                        style={{ width: `${f.importancia * 100}%`, background: "var(--c-accent-500)" }}
                      />
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>
        </>
      )}
    </>
  );
}
